// src/pages/Tickets/TicketMandatoryFieldList.jsx
// -----------------------------------------------------------------------------
// Component screen managing Ticket Mandatory Fields (Ticket_Mandatory_Field_TBL)
// Performs full CRUD (Read, Create, Update, Delete) via ticketMandatoryFieldService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import ticketMandatoryFieldService from '../../api/ticketMandatoryFieldService';
import projectService from '../../api/projectService';
import ticketStatusService from '../../api/ticketStatusService';
import { mockStatuses } from '../../api/ticketService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';

export default function TicketMandatoryFieldList() {
  const [projects, setProjects] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [currentFormValues, setCurrentFormValues] = useState({
    projectID: '',
    statusID: '',
    fieldName: '',
    isMandatoryFlag: false,
  });

  // Fetch projects and all statuses on mount
  useEffect(() => {
    projectService
      .getAll()
      .then((res) => setProjects(res.data || []))
      .catch(() => {});

    ticketStatusService
      .getAll()
      .then((res) => setStatuses(res.data || []))
      .catch(() => {});
  }, []);

  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(ticketMandatoryFieldService, {
    idKey: 'mandatoryFieldID',
    itemName: 'Mandatory Field',
    initialSort: { key: 'mandatoryFieldID', direction: 'asc' },
  });

  // Synchronize cascading filters when selecting a record for editing
  useEffect(() => {
    if (selectedRecord) {
      setCurrentFormValues({
        ...selectedRecord,
        projectID: selectedRecord.projectID || '',
        statusID: selectedRecord.statusID || '',
        fieldName: selectedRecord.fieldName || '',
        isMandatoryFlag: !!selectedRecord.isMandatoryFlag,
      });

      const projID = Number(selectedRecord.projectID);
      if (projID) {
        projectService.getStatuses(projID)
          .then((res) => setFilteredStatuses(res.data || []))
          .catch(() => {});
      }
    } else {
      setCurrentFormValues({
        projectID: '',
        statusID: '',
        fieldName: '',
        isMandatoryFlag: false,
      });
      setFilteredStatuses([]);
    }
  }, [selectedRecord]);

  // Handles dropdown cascades during input changes
  const handleFieldChange = async (fieldName, value, nextFormData) => {
    let updatedValues = { ...nextFormData };
    if (fieldName === 'projectID') {
      const projID = Number(value);
      if (!projID) {
        setFilteredStatuses([]);
        updatedValues.statusID = '';
        setCurrentFormValues(updatedValues);
        return;
      }
      try {
        const statsRes = await projectService.getStatuses(projID);
        const projStats = statsRes.data || [];
        setFilteredStatuses(projStats);
        updatedValues.statusID = projStats.length > 0 ? projStats[0].statusID : '';
        setCurrentFormValues(updatedValues);
      } catch (err) {
        console.error("Failed to load project statuses dynamically:", err);
      }
    }
  };

  // Schema configuration for Ticket_Mandatory_Field_TBL
  const mandatoryFields = useMemo(() => [
    {
      name: 'mandatoryFieldID',
      label: 'ID',
      type: 'integer',
      isPrimaryKey: true,
      readOnly: true,
    },
    {
      name: 'projectID',
      label: 'Project',
      type: 'select',
      required: true,
      options: projects.map((p) => ({ label: `${p.projectCode} — ${p.projectName}`, value: p.projectID })),
    },
    {
      name: 'statusID',
      label: 'Ticket Status',
      type: 'select',
      required: true,
      options: filteredStatuses.map((s) => ({ label: s.statusName, value: s.statusID })),
    },
    {
      name: 'fieldName',
      label: 'Field Name',
      type: 'varchar',
      required: true,
      maxLength: 100,
      placeholder: 'e.g. Resolution_Notes, Description, Severity',
    },
    {
      name: 'isMandatoryFlag',
      label: 'Is Mandatory Flag',
      type: 'checkbox',
      required: false,
      placeholder: 'Flag as mandatory before moving to status',
      defaultValue: false,
    },
  ], [projects, filteredStatuses]);

  const columns = [
    { key: 'mandatoryFieldID', label: 'ID', sortable: true, width: '80px' },
    {
      key: 'projectID',
      label: 'Project',
      sortable: true,
      render: (row) => {
        const proj = projects.find((p) => Number(p.projectID) === Number(row.projectID));
        return proj ? `${proj.projectCode} — ${proj.projectName}` : `-`;
      },
    },
    {
      key: 'statusID',
      label: 'Ticket Status',
      sortable: true,
      render: (row) => {
        const status = statuses.find((s) => Number(s.statusID) === Number(row.statusID))
          || mockStatuses.find((s) => Number(s.statusID) === Number(row.statusID));
        return status ? status.statusName : `-`;
      },
    },
    { key: 'fieldName', label: 'Field Name', sortable: true },
    {
      key: 'isMandatoryFlag',
      label: 'Is Mandatory Flag',
      sortable: true,
      render: (row) => row.isMandatoryFlag ? (
        <span style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
          Mandatory
        </span>
      ) : (
        <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
          Optional
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Ticket Mandatory Fields</h1>
          <div className="subtitle">
            Configure fields that must be completed before tickets transition to specific statuses.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Rule
        </button>
      </div>

      {/* Main Grid Table List */}
      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="mandatoryFieldID"
        emptyMessage="No mandatory field rules found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Row Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Reusable Form Modal */}
      {isFormOpen && (
        <TableForm
          fields={mandatoryFields}
          initialValues={currentFormValues}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          onFieldChange={handleFieldChange}
          itemName="Mandatory Field Rule"
        />
      )}

      {/* Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Mandatory Field Rule"
        itemLabel={selectedRecord ? `mandatory field rule for ${selectedRecord.fieldName}` : 'Mandatory Field Rule'}
        deleting={deleting}
      />
    </div>
  );
}
